import {putTourData, getTourData, putGeneralData, getGeneralData, clearLocale, putBulkLocale, getLocaleString} from '../../../helper/localStorage'
import {validateVersion} from "../../../helper/uiFunctions";
import SarasErpApi from '../../../helper/SarasErpApi';

// Share startup migration and serialize local count writes across hook instances.
let recentMenuMigration = null;
let recentMenuClickQueue = Promise.resolve();

const useSarasStore = () => {

    const setRecentMenuChanged = async () => {
        const result = await getGeneralData("top_menu_change_count");
        await putGeneralData("top_menu_change_count", (result || 0) + 1);
    }

    const getRecentMenuChangeCount = (onSuccess) => {
        requestData("top_menu_change_count", onSuccess);
    }

    const syncRecentMenus = (serverMigrationDone, restoreMenus) => {
        if(recentMenuMigration) return recentMenuMigration;

        recentMenuMigration = recentMenuClickQueue.then(async () => {
            const localMigrationDone = await getGeneralData("recent_menu_migration_done");
            const menus = await getGeneralData("app_menu");
            // File existence on the server is authoritative, including when the browser flag is stale.
            await putGeneralData("recent_menu_migration_done", serverMigrationDone);
            if(serverMigrationDone === 1){
                if(localMigrationDone !== 1 || !menus) await restoreMenus();
                return;
            }

            // Capture legacy counts before any server menu response replaces the browser cache.
            const recent = menus ? menus.find(menu => menu.MenuType === "f") : null;
            const recentMenus = recent ? recent.ChildMenus.map(menu => ({
                MenuLink: menu.MenuLink,
                UseCount: menu.UseCount
            })) : [];

            const response = await SarasErpApi.post('core', {
                actionName: "menu",
                actionId: "save",
                actionType: "recent-menu-migrate",
                dataList: {recentMenus: JSON.stringify(recentMenus)}
            });
            if(response.data.isError !== 0 || response.data.isSignedIn !== 1){
                throw new Error("Recent menu migration failed.");
            }

            await putGeneralData("recent_menu_migration_done", 1);
            // Hold new clicks until restoration finishes so a late menu response cannot erase them.
            await restoreMenus();
        }).catch(e => {
            console.error("[RecentMenuSync] Migration/restoration failed", e);
            throw e;
        }).finally(() => {
            recentMenuMigration = null;
        });

        return recentMenuMigration;
    }

    const setRecentMenu = (menuClicked) => {
        //add the menu to fav
        const migration = recentMenuMigration;
        recentMenuClickQueue = recentMenuClickQueue.then(async () => {
            // Avoid including a click in both the legacy upload and a separate increment.
            if(migration) await migration.catch(e => console.error("Recent menu initialization failed; continuing local click tracking.", e));
            const result = await getGeneralData("app_menu");
            let searchTopMenu = result.filter(
                function(value){ return value.MenuType === "f" }
            );
  
            if(searchTopMenu && searchTopMenu.length > 0){
                let existingTopMenu = searchTopMenu[0].ChildMenus.filter(
                    function(value){ return value.MenuLink === menuClicked.MenuLink }
                );

                if(existingTopMenu && existingTopMenu.length > 0){
                    //only increase the use count
                    existingTopMenu[0].UseCount = existingTopMenu[0].UseCount + 1;
                }
                else{
                    let menuUsed = {...menuClicked};
                    //add new menu and set use count to 1
                    menuUsed.UseCount = 1;
                    searchTopMenu[0].ChildMenus.push(menuUsed);
                }

                await putGeneralData("app_menu", result);
                await setRecentMenuChanged();

                // A failed migration leaves legacy data local; do not create the server file early.
                if(await getGeneralData("recent_menu_migration_done") === 1){
                    SarasErpApi.post('core', {
                        actionName: "menu",
                        actionId: "save",
                        actionType: "recent-menu-click",
                        dataList: {menuLink: menuClicked.MenuLink}
                    }).then(response => {
                        if(response.data.isError !== 0 || response.data.isSignedIn !== 1){
                            throw new Error("Recent menu click save failed.");
                        }
                    }).catch(e => console.error("Could not persist recent menu click.", e));
                }
            }
  
            // console.log("top menu", searchTopMenu, result);
        }).catch(e => {
            // Other error such as a string was thrown
            console.error("Could not update local recent menus.", e);
        });

        return recentMenuClickQueue;
    }

    //this will be used for comparing if the version of the app has changed
    //whenever version changes system will auto signout the users and they will need to reauthenticate
    //comparison will be done between the value received from server in the server attributes with the value in local store
    //auto signout will be performed only when major or minor version changes, not when patch version change
    //format 3.1.1 <- major.minor.patch
    const setAppVersion = (version, callback) => {
        //validate the version
        if(validateVersion(version)){
            setData("app_version", version, callback);
        }
        else{
            //return failure
            callback(0);
        }
    }

    const getAppVersion = (onSuccess) => {
        requestData("app_version", onSuccess);
    }

    const setUDBKey = (udbKey, callback) => {
        setData("unique_db_key", udbKey, callback);
    }

    const getUDBKey = (onSuccess) => {
        requestData("unique_db_key", onSuccess);
    }

    const setAppMenu = (menuData, callback) => {
        // Notify Recent consumers only after the restored menu has been saved.
        return putGeneralData("app_menu", menuData).then(async result => {
            await setRecentMenuChanged();
            if(callback) callback(result);
        }).catch(e => {
            console.error("Could not store app menus.", e);
            throw e;
        });
    }

    const getAppMenu = (onSuccess) => {
        requestData("app_menu", onSuccess);
    }

    const setTourData = (key, value, callback) => {
        // console.log("local data set", key, value);

        const promise = putTourData(key, value);

        promise.then((result) => {
            // console.log("local data inside promise", result);
            if(callback) callback(result);
        }).catch(e => {
            // Other error such as a string was thrown
            // console.error (e);
        });
    }

    const requestTourData = (key, callback) => {
        //return empty if key is not a string or is empty
        if(!key || typeof key != "string") {
            if(callback) callback(key, "");
        }
        else{
            const promise = getTourData(key.trim());

            promise.then((result) => {
                // console.log("local data inside promise", result);
                if(callback) callback(key, result);
            }).catch(e => {
                // Other error such as a string was thrown
                // console.error (e);
            });
        }
    }
    
    const setData = (key, value, callback) => {
        // console.log("local data set", key, value);

        const promise = putGeneralData(key, value);

        promise.then((result) => {
            // console.log("local data inside promise", result);
            if(callback) callback(result);
        }).catch(e => {
            // Other error such as a string was thrown
            // console.error (e);
        });
    }

    const requestData = (key, callback) => {
        //return empty if key is not a string or is empty
        if(!key || typeof key != "string") {
            if(callback) callback(key, "");
        }
        else{
            const promise = getGeneralData(key.trim());

            promise.then((result) => {
                // console.log("local data inside promise", result);
                if(callback) callback(key, result);
            }).catch(e => {
                // Other error such as a string was thrown
                // console.error (e);
            });
        }
    }

    const setStrings = (data, emptyAll, callback) => {
        if(emptyAll === 1){
            const promise = clearLocale();

            promise.then(() => {
                requestBulkAddLocal(data, callback);
            }).catch(e => {
                // Other error such as a string was thrown
                // console.error (e);
            });
        }
        else{
            requestBulkAddLocal(data, callback);
        }
    }

    const requestBulkAddLocal = (data, callback) => {
        const promise = putBulkLocale(data);

        promise.then((result) => {
            if(callback) callback(result);
        }).catch(e => {
            // Other error such as a string was thrown
            // console.error (e);
        });
    }

    const requestString = (key, callback)=>{
        //return empty if key is not a string or is empty
        if(!key || typeof key != "string") {
            if(callback) callback(key, "");
        }
        else{
            const promise = getLocaleString(key.trim());

            promise.then((result) => {
                // console.log("local data inside promise", result);
                if(callback) callback(key, result);
            }).catch(e => {
                // Other error such as a string was thrown
                // console.error (e);
            });
        }
    }

    const returnObj = {
        setData, 
        requestData, 
        setTourData,
        requestTourData,
        setStrings, 
        requestString,
        setAppVersion,
        getAppVersion,
        setAppMenu,
        getAppMenu,
        setRecentMenu,
        syncRecentMenus,
        getRecentMenuChangeCount,
        getUDBKey,
        setUDBKey
    }

    return returnObj;
}

export default useSarasStore;
